const bytesToSize = (bytes: number): string => {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  if (bytes === 0) {
    return '0 B';
  }

  const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))));

  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

/**
 * Removes all markdown formatting from text
 * Handles all markdown features supported by the MarkdownText component:
 * - Bold/strong (**text** or __text__)
 * - Italic/emphasis (*text* or _text_)
 * - Links ([text](url))
 * - Headings (# text)
 * - Code blocks (```code```) and inline code (`code`)
 * - Lists (ordered and unordered)
 * - Custom highlight syntax (=text=)
 * - Horizontal rules (---, ***, ___)
 */
const removeMarkdown = (text: string): string => {
  if (!text) return '';

  let plainText = text;

  // Remove code blocks
  plainText = plainText.replace(/```[\s\S]*?```/g, (match) => {
    // Extract the code content without the backticks
    const code = match.slice(3, -3).trim();
    return code;
  });

  // Remove inline code
  plainText = plainText.replace(/`([^`]+)`/g, '$1');

  // Remove headings
  plainText = plainText.replace(/^(#{1,6})\s+(.+)$/gm, '$2');

  // Remove bold/strong
  plainText = plainText.replace(/\*\*([^*]+)\*\*/g, '$1');
  plainText = plainText.replace(/__([^_]+)__/g, '$1');

  // Remove italic/emphasis
  plainText = plainText.replace(/\*([^*]+)\*/g, '$1');
  plainText = plainText.replace(/_([^_]+)_/g, '$1');

  // Remove links but keep the text
  plainText = plainText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove list markers
  plainText = plainText.replace(/^(\s*)[-*+]\s+/gm, '$1');
  plainText = plainText.replace(/^(\s*)\d+\.\s+/gm, '$1');

  // Remove horizontal rules
  plainText = plainText.replace(/^([-*_])\1{2,}$/gm, '');

  // Remove custom highlight syntax
  plainText = plainText.replace(/=([^=]+)=/g, '$1');

  // Remove HTML tags (for safety)
  plainText = plainText.replace(/<[^>]*>/g, '');

  // Normalize whitespace
  plainText = plainText.replace(/\n{3,}/g, '\n\n');

  return plainText.trim();
};

/**
 * Formats a money amount held in minor units (e.g. cents/grosze) for display,
 * appending the currency code — e.g. formatMoney({ amount: 4470, currency: 'PLN' }) → "44.70 PLN".
 */
const formatMoney = (money: { amount: number; currency: string }, locale?: string): string => {
  const value = (money.amount ?? 0) / 100;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
  return `${formatted} ${money.currency}`;
};

/**
 * Formats a percentage number for display — e.g. formatPercent(15) → "15%",
 * formatPercent(12.5) → "12.5%". Drops a trailing ".0".
 */
const formatPercent = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text}%`;
};

const normalizeWhitespace = (text: string | null | undefined): string => {
  if (!text) return '';

  return text
    .replace(/\u00A0+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
};

const humaniseAgentKind = (agentKind: string | null): string | null => {
  if (!agentKind) return null;

  const words = agentKind
    .split(/_+/)
    .filter((part) => part.length > 0 && part !== 'GENERATOR')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase());

  return words.length > 0 ? words.join(' ') : null;
};

export {
  bytesToSize,
  formatMoney,
  formatPercent,
  humaniseAgentKind,
  normalizeWhitespace,
  removeMarkdown
};
